(ns frontend.view.modals.ai-deal-one-pager.event
  "Generation lifecycle events for the AI deal one-pager modal."
  (:require
   [clojure.string :as string]
   [frontend.analytics :as analytics]
   [frontend.logging :as logging]
   [frontend.ui.ai-summary-configuration.model :as configuration-model]
   [frontend.ui.ai-summary-configuration.upload :as configuration-upload]
   [frontend.utils :as fu]
   [frontend.view.modals.ai-deal-one-pager.model :as model]
   [frontend.view.modals.events :as modal-events]
   [fx.ajax :as ajax]
   [oops.core :as oops]
   [re-frame.core :as rf]))

(def poll-wait-ms 2000)

(defn- path
  [& keys]
  (into model/root-path keys))

(defn- poll-now-effect
  [generation]
  (when (model/generation-active? generation)
    {:fx [[:dispatch [::poll (:id generation)]]]}))

(defn- poll-later-effect
  [generation]
  (when (model/generation-active? generation)
    {:fx [[:dispatch-later
           {:ms poll-wait-ms
            :dispatch [::poll (:id generation)]}]]}))

(defn- lifecycle-details
  [lifecycle-phase generation]
  {:phase lifecycle-phase
   :generation-id (:id generation)
   :deal-id (:deal-id generation)
   :status (:status generation)})

(rf/reg-fx
 ::download-attachment
 fu/download-attachment!)

(rf/reg-fx
 ::track
 analytics/event)

(rf/reg-fx
 ::log-lifecycle
 (fn [details]
   (logging/info "AI deal one-pager lifecycle" details)))

(defn- open-fx
  [{db :db} [_ deal-id pipeline-id]]
  {:db (assoc-in db model/root-path (model/initial-state deal-id pipeline-id))
   :fx [[:dispatch [::load-configurations]]
        [:dispatch [::load-latest]]]})

(rf/reg-event-fx
 ::open
 open-fx)

(defn- load-configurations-fx
  [{db :db} [_ preferred-id]]
  (let [{:keys [deal-id pipeline-id]} (get-in db model/root-path)]
    {::ajax/send
     {:operation :ai-summary-configuration/list
      :body {:pipeline-id pipeline-id}
      :on-ok [::on-configurations deal-id preferred-id]
      :on-error [::on-request-error
                 {:phase :library
                  :deal-id deal-id}]}}))

(rf/reg-event-fx
 ::load-configurations
 load-configurations-fx)

(defn- load-configuration-fx
  [{db :db} [_ configuration-id]]
  (let [deal-id (get-in db (path :deal-id))]
    {:db (-> db
             (assoc-in (path :configuration-detail-state) :loading)
             (assoc-in (path :configuration-detail) nil)
             (assoc-in (path :draft) nil)
             (assoc-in (path :saved-draft) nil)
             (assoc-in (path :copy-source-name) nil)
             (assoc-in (path :copy-previous-name) nil)
             (assoc-in (path :save-state) :idle))
     ::ajax/send
     {:operation :ai-summary-configuration/get
      :body {:configuration-id configuration-id}
      :on-ok [::on-configuration deal-id configuration-id]
      :on-error [::on-request-error
                 {:phase :configuration
                  :deal-id deal-id
                  :configuration-id configuration-id}]}}))

(rf/reg-event-fx
 ::load-configuration
 load-configuration-fx)

(defn- load-latest-fx
  [{db :db} _]
  (let [deal-id (get-in db (path :deal-id))]
    {::ajax/send
     {:operation :ai-deal-one-pager/status
      :body {:deal-id deal-id}
      :on-ok [::on-latest deal-id]
      :on-error [::on-request-error
                 {:phase :load
                  :deal-id deal-id}]}}))

(rf/reg-event-fx
 ::load-latest
 load-latest-fx)

(defn- start-fx
  [{db :db} _]
  (let [state (get-in db model/root-path)
        deal-id (:deal-id state)
        configuration-id (:selected-configuration-id state)]
    (when (model/generation-ready? state)
      {:db (-> db
               (assoc-in (path :request-state) :starting)
               (assoc-in (path :generation) nil)
               (assoc-in (path :deduplicated?) false)
               (assoc-in (path :error-category) nil))
       ::ajax/send
       {:operation :ai-deal-one-pager/start
        :body {:deal-id deal-id
               :configuration-id configuration-id
               :overrides (model/run-overrides (:draft state))}
        :on-ok [::on-started]
        :on-error [::on-request-error
                   {:phase :start
                    :deal-id deal-id}]}
       ::log-lifecycle {:phase :start-requested
                        :deal-id deal-id
                        :configuration-id configuration-id}})))

(rf/reg-event-fx
 ::start
 start-fx)

(defn- generation-response
  [response]
  (get-in response [:body :generation]))

(defn- on-configurations-fx
  [{db :db} [_ expected-deal-id preferred-id response]]
  (let [state (get-in db model/root-path)
        configurations (get-in response [:body :configurations])
        restored-id (get-in state [:generation :configuration-id])
        selected-id (or (when (some (fn [configuration]
                                      (= preferred-id (:id configuration)))
                                    configurations)
                          preferred-id)
                        (when (some (fn [configuration]
                                      (= restored-id (:id configuration)))
                                    configurations)
                          restored-id)
                        (when (some (fn [configuration]
                                      (= (:selected-configuration-id state)
                                         (:id configuration)))
                                    configurations)
                          (:selected-configuration-id state))
                        (model/pipeline-default-id configurations
                                                   (:pipeline-id state)))]
    (if (= expected-deal-id (:deal-id state))
      (cond->
       {:db (-> db
                (assoc-in (path :library-state) :ready)
                (assoc-in (path :configurations) configurations)
                (assoc-in (path :selected-configuration-id) selected-id)
                (assoc-in (path :error-category) nil))}
        selected-id
        (assoc :fx [[:dispatch [::load-configuration selected-id]]]))
      {})))

(rf/reg-event-fx
 ::on-configurations
 on-configurations-fx)

(defn- select-configuration-fx
  [{db :db} [_ configuration-id]]
  (let [state (get-in db model/root-path)
        valid-id? (some (fn [configuration]
                          (= configuration-id (:id configuration)))
                        (:configurations state))]
    (when valid-id?
      {:db (assoc-in db (path :selected-configuration-id) configuration-id)
       :fx [[:dispatch [::load-configuration configuration-id]]]})))

(rf/reg-event-fx
 ::select-configuration
 select-configuration-fx)

(defn- on-configuration-fx
  [{db :db} [_ expected-deal-id expected-configuration-id response]]
  (let [state (get-in db model/root-path)
        configuration (get-in response [:body :configuration])
        draft (model/configuration-detail->draft configuration)]
    (if (and (= expected-deal-id (:deal-id state))
             (= expected-configuration-id
                (:selected-configuration-id state))
             (= expected-configuration-id (:id configuration)))
      {:db (-> db
               (assoc-in (path :configuration-detail-state) :ready)
               (assoc-in (path :configuration-detail) configuration)
               (assoc-in (path :draft) draft)
               (assoc-in (path :saved-draft) draft)
               (assoc-in (path :copy-source-name) nil)
               (assoc-in (path :copy-previous-name) nil)
               (assoc-in (path :save-state) :idle)
               (assoc-in (path :error-category) nil))}
      {})))

(rf/reg-event-fx
 ::on-configuration
 on-configuration-fx)

(defn- update-draft-fx
  [{db :db} [_ field value]]
  (let [draft-path (path :draft)
        db (-> db
               (assoc-in (conj draft-path field) value)
               (assoc-in (path :save-state) :idle))]
    {:db
     (case field
       :style-guideline-source
       (case value
         :ai-summary-configuration.style-guideline-source/none
         (-> db
             (assoc-in (conj draft-path :style-guideline-document-id) nil)
             (assoc-in (conj draft-path :style-guideline-document-filename) nil)
             (assoc-in (conj draft-path :style-reference-url) nil))

         :ai-summary-configuration.style-guideline-source/document
         (assoc-in db (conj draft-path :style-reference-url) nil)

         :ai-summary-configuration.style-guideline-source/url
         (-> db
             (assoc-in (conj draft-path :style-guideline-document-id) nil)
             (assoc-in (conj draft-path :style-guideline-document-filename) nil))

         db)

       db)}))

(rf/reg-event-fx
 ::update-draft
 update-draft-fx)

(defn- toggle-source-fx
  [{db :db} [_ source selected?]]
  {:db (update-in db
                  (path :draft :selected-data-sources)
                  (fn [selected]
                    ((if selected? conj disj) (set selected) source)))})

(rf/reg-event-fx
 ::toggle-source
 toggle-source-fx)

(defn- on-latest-fx
  [{db :db} [_ expected-deal-id response]]
  (let [generation (generation-response response)
        current-state (get-in db model/root-path)
        current-deal-id (:deal-id current-state)]
    (cond
      (not= expected-deal-id current-deal-id)
      {}

      (nil? generation)
      {:db (assoc-in db (path :request-state) :ready)}

      (= current-deal-id (:deal-id generation))
      (let [load-configuration?
            (and (= :ready (:library-state current-state))
                 (:configuration-id generation)
                 (not= (:configuration-id generation)
                       (:selected-configuration-id current-state)))]
        (cond->
         (merge
          {:db (-> db
                   (assoc-in (path :request-state) :ready)
                   (assoc-in (path :generation) generation)
                   (cond-> (:configuration-id generation)
                     (assoc-in (path :selected-configuration-id)
                               (:configuration-id generation))))
           ::log-lifecycle (lifecycle-details :latest-loaded generation)}
          (poll-now-effect generation))
          load-configuration?
          (update :fx
                  (fnil conj [])
                  [:dispatch [::load-configuration
                              (:configuration-id generation)]])))

      :else
      {})))

(rf/reg-event-fx
 ::on-latest
 on-latest-fx)

(defn- on-started-fx
  [{db :db} [_ response]]
  (let [generation (generation-response response)
        deduplicated? (get-in response [:body :deduplicated?])
        current-state (get-in db model/root-path)]
    (if (= (:deal-id current-state) (:deal-id generation))
      (merge
       {:db (-> db
                (assoc-in (path :request-state) :ready)
                (assoc-in (path :generation) generation)
                (assoc-in (path :deduplicated?) (boolean deduplicated?)))
        ::track "ai-deal-one-pager-started"
        ::log-lifecycle (assoc (lifecycle-details :started generation)
                               :deduplicated? (boolean deduplicated?))}
       (poll-now-effect generation))
      {})))

(rf/reg-event-fx
 ::on-started
 on-started-fx)

(defn- poll-fx
  [{db :db} [_ expected-generation-id]]
  (let [{:keys [deal-id generation]} (get-in db model/root-path)]
    (when (and (= expected-generation-id (:id generation))
               (model/generation-active? generation))
      {::ajax/send
       {:operation :ai-deal-one-pager/status
        :body {:deal-id deal-id
               :generation-id (:id generation)}
        :on-ok [::on-status]
        :on-error [::on-request-error
                   {:phase :poll
                    :deal-id deal-id
                    :generation-id (:id generation)}]}
       ::log-lifecycle (lifecycle-details :poll-requested generation)})))

(rf/reg-event-fx
 ::poll
 poll-fx)

(defn- on-status-fx
  [{db :db} [_ response]]
  (let [previous-generation (get-in db (path :generation))
        generation (generation-response response)
        completed-now? (and (not (model/generation-completed? previous-generation))
                            (model/generation-completed? generation))
        current-generation-id (:id previous-generation)]
    (if (= current-generation-id (:id generation))
      (merge
       {:db (-> db
                (assoc-in (path :request-state) :ready)
                (assoc-in (path :generation) generation)
                (assoc-in (path :error-category) nil))
        ::log-lifecycle (assoc (lifecycle-details :poll-response generation)
                               :next-poll? (model/generation-active? generation))}
       (poll-later-effect generation)
       (when completed-now?
         {::track "ai-deal-one-pager-completed"}))
      {})))

(rf/reg-event-fx
 ::on-status
 on-status-fx)

(defn- save-current-fx
  [{db :db} _]
  (let [{:keys [deal-id selected-configuration-id configuration-detail draft]
         :as state}
        (get-in db model/root-path)]
    (when (and (:owned-by-requester? configuration-detail)
               (model/draft-valid? draft)
               (model/draft-dirty? state))
      {:db (assoc-in db (path :save-state) :saving)
       ::ajax/send
       {:operation :entity/update-attributes
        :body {:db/id selected-configuration-id
               :data (model/configuration-update-data draft)}
        :on-ok [::on-configuration-saved deal-id selected-configuration-id]
        :on-error [::on-save-error deal-id selected-configuration-id]}})))

(rf/reg-event-fx
 ::save-current
 save-current-fx)

(defn- prepare-create-fx
  [{db :db} _]
  (let [state (get-in db model/root-path)]
    (when (= :ready (:library-state state))
      {:db (-> db
               (assoc-in (path :create-mode?) true)
               (assoc-in (path :create-previous-id)
                         (:selected-configuration-id state))
               (assoc-in (path :selected-configuration-id) nil)
               (assoc-in (path :configuration-detail-state) :ready)
               (assoc-in (path :configuration-detail)
                         {:owned-by-requester? true})
               (assoc-in (path :draft) (configuration-model/blank-draft))
               (assoc-in (path :saved-draft) nil)
               (assoc-in (path :copy-source-name) nil)
               (assoc-in (path :copy-previous-name) nil)
               (assoc-in (path :uploading-field) nil)
               (assoc-in (path :save-state) :idle))})))

(rf/reg-event-fx
 ::prepare-create
 prepare-create-fx)

(defn- cancel-create-fx
  [{db :db} _]
  (let [state (get-in db model/root-path)
        previous-id (:create-previous-id state)]
    (when (model/create-mode? state)
      (cond-> {:db (-> db
                       (assoc-in (path :create-mode?) false)
                       (assoc-in (path :create-previous-id) nil)
                       (assoc-in (path :configuration-detail-state) :idle)
                       (assoc-in (path :configuration-detail) nil)
                       (assoc-in (path :draft) nil)
                       (assoc-in (path :saved-draft) nil)
                       (assoc-in (path :uploading-field) nil)
                       (assoc-in (path :save-state) :idle))}
        previous-id
        (assoc :fx [[:dispatch [::select-configuration previous-id]]])))))

(rf/reg-event-fx
 ::cancel-create
 cancel-create-fx)

(defn- save-new-fx
  [{db :db} _]
  (let [{:keys [deal-id draft uploading-field] :as state}
        (get-in db model/root-path)]
    (when (and (model/create-mode? state)
               (nil? uploading-field)
               (model/draft-valid? draft))
      {:db (assoc-in db (path :save-state) :saving)
       ::ajax/send
       {:operation :entity/create-entity
        :body (model/created-configuration-data draft)
        :on-ok [::on-configuration-created deal-id]
        :on-error [::on-save-error deal-id nil]}})))

(rf/reg-event-fx
 ::save-new
 save-new-fx)

(defn- prepare-save-as-new-fx
  [{db :db} _]
  (let [{:keys [configuration-detail draft]}
        (get-in db model/root-path)
        source-name (:configuration-name configuration-detail)
        previous-name (:configuration-name draft)
        copy-name (if (configuration-model/copy-name-valid? draft source-name)
                    previous-name
                    "")]
    (when (and configuration-detail draft)
      {:db (-> db
               (assoc-in (path :copy-source-name) source-name)
               (assoc-in (path :copy-previous-name) previous-name)
               (assoc-in (path :draft :configuration-name) copy-name)
               (assoc-in (path :save-state) :idle))})))

(rf/reg-event-fx
 ::prepare-save-as-new
 prepare-save-as-new-fx)

(defn- cancel-save-as-new-fx
  [{db :db} _]
  (let [state (get-in db model/root-path)]
    (when (model/copy-mode? state)
      {:db (-> db
               (assoc-in (path :draft :configuration-name)
                         (:copy-previous-name state))
               (assoc-in (path :copy-source-name) nil)
               (assoc-in (path :copy-previous-name) nil)
               (assoc-in (path :save-state) :idle))})))

(rf/reg-event-fx
 ::cancel-save-as-new
 cancel-save-as-new-fx)

(defn- save-as-new-fx
  [{db :db} _]
  (let [{:keys [deal-id configuration-detail draft]}
        (get-in db model/root-path)]
    (when (and configuration-detail
               (model/draft-valid? draft)
               (model/copy-name-valid? (get-in db model/root-path)))
      {:db (assoc-in db (path :save-state) :saving)
       ::ajax/send
       {:operation :entity/create-entity
        :body (model/new-configuration-data configuration-detail draft)
        :on-ok [::on-configuration-created deal-id]
        :on-error [::on-save-error deal-id nil]}})))

(rf/reg-event-fx
 ::save-as-new
 save-as-new-fx)

(defn- on-configuration-saved-fx
  [{db :db} [_ expected-deal-id configuration-id _response]]
  (let [state (get-in db model/root-path)]
    (if (= expected-deal-id (:deal-id state))
      {:db (assoc-in db (path :save-state) :saved)
       :fx [[:dispatch [::load-configurations configuration-id]]]
       ::track "ai-summary-configuration-updated"}
      {})))

(rf/reg-event-fx
 ::on-configuration-saved
 on-configuration-saved-fx)

(defn- on-configuration-created-fx
  [{db :db} [_ expected-deal-id response]]
  (let [state (get-in db model/root-path)
        configuration-id (get-in response [:body :db/id])]
    (if (and (= expected-deal-id (:deal-id state))
             configuration-id)
      {:db (-> db
               (assoc-in (path :save-state) :saved)
               (assoc-in (path :create-mode?) false)
               (assoc-in (path :create-previous-id) nil))
       :fx [[:dispatch [::load-configurations configuration-id]]]
       ::track "ai-summary-configuration-created"}
      {})))

(rf/reg-event-fx
 ::on-configuration-created
 on-configuration-created-fx)

(defn- on-save-error-fx
  [{db :db} [_ expected-deal-id expected-configuration-id error]]
  (let [state (get-in db model/root-path)]
    (if (and (= expected-deal-id (:deal-id state))
             (or (nil? expected-configuration-id)
                 (= expected-configuration-id
                    (:selected-configuration-id state))))
      (do
        (logging/error "AI summary configuration save failed" error)
        {:db (assoc-in db (path :save-state) :failed)})
      {})))

(rf/reg-event-fx
 ::on-save-error
 on-save-error-fx)

(defn- upload-document-fx
  [{db :db} [_ field file]]
  (let [state (get-in db model/root-path)
        filename (when file (oops/oget file "name"))
        template? (= field :document-template)
        valid? (and filename
                    (or (not template?)
                        (string/ends-with? (string/lower-case filename)
                                           ".docx")))]
    (if-not valid?
      {:db (-> db
               (assoc-in (path :uploading-field) nil)
               (assoc-in (path :save-state) :invalid-template))}
      {:db (-> db
               (assoc-in (path :uploading-field) field)
               (assoc-in (path :save-state) :idle))
       ::configuration-upload/document
       {:file file
        :on-success [::document-uploaded (:deal-id state) field filename]
        :on-error [::document-upload-failed (:deal-id state) field]}})))

(rf/reg-event-fx
 ::upload-document
 upload-document-fx)

(defn- document-uploaded-fx
  [{db :db} [_ expected-deal-id field filename attachment]]
  (let [state (get-in db model/root-path)
        attachment-id (:db/id attachment)
        attachment-filename (or (:attachment/filename attachment) filename)]
    (if (= expected-deal-id (:deal-id state))
      {:db
       (case field
         :style-guideline-document
         (-> db
             (assoc-in (path :draft :style-guideline-source)
                       :ai-summary-configuration.style-guideline-source/document)
             (assoc-in (path :draft :style-guideline-document-id)
                       attachment-id)
             (assoc-in (path :draft :style-guideline-document-filename)
                       attachment-filename)
             (assoc-in (path :draft :style-reference-url) nil)
             (assoc-in (path :uploading-field) nil))

         :document-template
         (-> db
             (assoc-in (path :draft :document-template-id) attachment-id)
             (assoc-in (path :draft :document-template-filename)
                       attachment-filename)
             (assoc-in (path :uploading-field) nil))

         (assoc-in db (path :uploading-field) nil))}
      {})))

(rf/reg-event-fx
 ::document-uploaded
 document-uploaded-fx)

(defn- document-upload-failed-fx
  [{db :db} [_ expected-deal-id field error]]
  (let [state (get-in db model/root-path)]
    (if (and (= expected-deal-id (:deal-id state))
             (= field (:uploading-field state)))
      (do
        (logging/error "AI summary configuration document upload failed" error)
        {:db (-> db
                 (assoc-in (path :uploading-field) nil)
                 (assoc-in (path :save-state) :upload-failed))})
      {})))

(rf/reg-event-fx
 ::document-upload-failed
 document-upload-failed-fx)

(defn- on-request-error-fx
  [{db :db} [_ {:keys [phase deal-id generation-id]} error]]
  (let [current-state (get-in db model/root-path)
        current-generation-id (get-in current-state [:generation :id])
        current-request? (and (= deal-id (:deal-id current-state))
                              (or (nil? generation-id)
                                  (= generation-id current-generation-id)))]
    (if current-request?
      (do
        (logging/error "AI deal one-pager request failed" error)
        (merge
         {:db (cond-> (assoc-in db
                                (path :error-category)
                                (model/classify-error error))
                (= phase :library)
                (assoc-in (path :library-state) :failed)

                (= phase :configuration)
                (assoc-in (path :configuration-detail-state) :failed)

                (not (contains? #{:library :configuration} phase))
                (assoc-in (path :request-state)
                          (case phase
                            :poll :poll-failed
                            :load :load-failed
                            :start-failed)))
          ::track "ai-deal-one-pager-failed"
          ::log-lifecycle {:phase :request-failed
                           :request-phase phase
                           :deal-id deal-id
                           :generation-id generation-id
                           :error-category (model/classify-error error)}}))
      {})))

(rf/reg-event-fx
 ::on-request-error
 on-request-error-fx)

(defn- retry-fx
  [_ _]
  {:fx [[:dispatch [::start]]]})

(rf/reg-event-fx
 ::retry
 retry-fx)

(defn- prepare-new-fx
  [{db :db} _]
  {:db (-> db
           (assoc-in (path :request-state) :ready)
           (assoc-in (path :generation) nil)
           (assoc-in (path :deduplicated?) false)
           (assoc-in (path :error-category) nil))})

(rf/reg-event-fx
 ::prepare-new
 prepare-new-fx)

(defn- download-fx
  [_ [_ attachment-id]]
  {::download-attachment attachment-id
   ::track "ai-deal-one-pager-downloaded"})

(rf/reg-event-fx
 ::download
 download-fx)

(defn- close-fx
  [{db :db} [_ close-modal!]]
  {:db (assoc-in db model/root-path nil)
   ::modal-events/close-modal close-modal!})

(rf/reg-event-fx
 ::close
 close-fx)
